import React, { useMemo } from "react"

import { ChartFigure, type ChartTable } from "@coval/ui/chart-figure"
import { Tooltip as CovalTooltip } from "@coval/ui/tooltip"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import {
  PRIMARY_BAR_SERIES_COLOR,
  seriesChipTones,
} from "@/components/charts/seriesPalette"

interface BinaryCompareByPctDatum {
  label: string
  value: number
  color?: string
}

interface BinaryCompareByPctChartProps {
  data: BinaryCompareByPctDatum[]
  height?: number
  /** e.g. "Success %" or "% Yes" */
  valueLabel?: string
  /** Accessible name of the chart; defaults to the value label by group. */
  label?: string
}

const dividerColor = "var(--coval-border)"
const tickLabelColor = "var(--coval-text-muted)"
const defaultBarColor = PRIMARY_BAR_SERIES_COLOR

const BinaryYAxisTick = ({
  x,
  y,
  payload,
  chartData,
}: {
  x?: number
  y?: number
  payload?: { value: string }
  chartData: Array<{
    id: string
    label: string
    fullLabel: string
    color?: string
  }>
}) => {
  const entry = chartData.find((d) => d.id === payload?.value)
  const displayText = entry?.label ?? payload?.value
  const fullLabel = entry?.fullLabel ?? payload?.value ?? ""
  const color = entry?.color ?? tickLabelColor
  return (
    <CovalTooltip content={fullLabel} side="top">
      <text x={x} y={y} dy={4} textAnchor="end" fontSize={11} fill={color}>
        {displayText}
      </text>
    </CovalTooltip>
  )
}

/**
 * Horizontal bar chart: one bar per compare dimension, value = percentage of target (0–100).
 * Matches the style of the binary overview (horizontal bars, Y = categories, X = value).
 */
export const BinaryCompareByPctChart: React.FC<
  BinaryCompareByPctChartProps
> = ({ data, height = 164, valueLabel = "Success %", label }) => {
  const chartLabel = label ?? `${valueLabel} by group`
  const chartData = useMemo(
    (): (BinaryCompareByPctDatum & { fullLabel: string; id: string })[] =>
      data.map((d, i) => ({
        ...d,
        fullLabel: d.label,
        id: `${d.label}-${i}`,
        label: d.label.length > 16 ? `${d.label.slice(0, 15)}…` : d.label,
      })),
    [data],
  )

  const summary = useMemo(() => {
    if (data.length === 0) {
      return undefined
    }
    const sorted = [...data].sort((a, b) => b.value - a.value)
    const high = sorted[0]
    const low = sorted[sorted.length - 1]
    if (sorted.length === 1) {
      return `${high.label}: ${high.value.toFixed(0)}%.`
    }
    return `Highest is ${high.label} at ${high.value.toFixed(0)}%; lowest is ${low.label} at ${low.value.toFixed(0)}%.`
  }, [data])

  const table = useMemo(
    (): ChartTable => ({
      columns: [
        { key: "label", header: "Group" },
        { key: "value", header: valueLabel, numeric: true },
      ],
      rows: chartData.map((row) => ({
        key: row.id,
        cells: { label: row.fullLabel, value: `${row.value.toFixed(0)}%` },
      })),
    }),
    [chartData, valueLabel],
  )

  if (chartData.length === 0) {
    return null
  }

  const barSize = Math.max(16, Math.min(28, (height - 50) / chartData.length))

  return (
    <ChartFigure
      label={chartLabel}
      summary={summary}
      table={table}
      className="w-full mt-1"
      style={{ height }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 5, right: 60, left: 0, bottom: 10 }}
          barCategoryGap="12%"
          role="img"
          aria-label={chartLabel}
        >
          <CartesianGrid
            horizontal={false}
            vertical
            stroke={dividerColor}
            strokeDasharray="none"
          />
          <XAxis
            type="number"
            domain={[0, 100]}
            axisLine={{ stroke: dividerColor, strokeWidth: 1 }}
            tickLine={false}
            tick={{ fontSize: 10, fill: tickLabelColor }}
            ticks={[0, 25, 50, 75, 100]}
            tickFormatter={(v) => `${v}%`}
            height={28}
            label={{
              value: valueLabel,
              position: "insideBottom",
              offset: -5,
              style: {
                fontSize: 11,
                fill: "var(--coval-text)",
                fontWeight: 600,
              },
            }}
          />
          <YAxis
            type="category"
            dataKey="id"
            axisLine={false}
            tickLine={false}
            width={90}
            tick={<BinaryYAxisTick chartData={chartData} />}
          />
          <Tooltip
            formatter={(value: number) => [
              `${Number(value).toFixed(0)}%`,
              valueLabel,
            ]}
            labelFormatter={(label: string) => label}
            contentStyle={{
              borderRadius: "6px",
              fontSize: "12px",
              padding: "6px 10px",
              backgroundColor: "var(--surface-default)",
              border: `1px solid ${dividerColor}`,
              color: "var(--coval-text)",
            }}
            labelStyle={{
              color: "var(--coval-text)",
            }}
            itemStyle={{
              color: "var(--coval-text)",
            }}
            cursor={{ fill: "var(--coval-border)" }}
          />
          <Bar dataKey="value" radius={[0, 2, 2, 0]} barSize={barSize}>
            {chartData.map((entry) => {
              const base = entry.color ?? defaultBarColor
              const fill = entry.color
                ? (seriesChipTones(entry.color)?.light ?? base)
                : base
              return <Cell key={entry.id} fill={fill} />
            })}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartFigure>
  )
}
